import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../services/api";
import RefreshButton from "../RefreshButton/RefreshButton";

function formatNumber(n) {
  if (n === null || n === undefined || n === "") return "0";
  const v = typeof n === "number" ? n : parseInt(n, 10);
  return Number.isFinite(v) ? v.toLocaleString() : "0";
}

function copyJson(obj) {
  try {
    navigator.clipboard.writeText(JSON.stringify(obj, null, 2));
  } catch {
    /* no-op */
  }
}

function CollapsibleBlock({ title, defaultOpen = false, stat, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="prompt-section" style={{ marginBottom: "0.75rem" }}>
      <div
        className="section-header"
        style={{ cursor: "pointer", userSelect: "none" }}
        onClick={() => setOpen((v) => !v)}
      >
        <h4>{open ? "▾" : "▸"} {title}</h4>
        {stat && <span className="section-stat">{stat}</span>}
      </div>
      {open && children}
    </div>
  );
}

function statusBadge(status) {
  const map = {
    completed: { color: "#0a0", label: "✅ completed" },
    failed: { color: "#c33", label: "❌ failed" },
    needs_clarification: { color: "#c80", label: "❓ needs clarification" },
    running: { color: "#06c", label: "⏳ running" },
  };
  const m = map[status] || { color: "#888", label: status || "unknown" };
  return (
    <span className="token-badge" style={{ color: m.color }}>{m.label}</span>
  );
}

function CallbackReentrySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [maxDepth, setMaxDepth] = useState("2");
  const [maxIterations, setMaxIterations] = useState("4");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/settings");
      const settings = data.settings || {};
      setEnabled((settings.callback_reentry_aura_enabled?.value || "true") !== "false");
      setMaxDepth(settings.callback_reentry_max_depth?.value || "2");
      setMaxIterations(settings.callback_reentry_max_iterations?.value || "4");
    } catch (err) {
      console.error("Failed to fetch callback re-entry settings:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await apiClient.put("/settings", {
        settings: {
          callback_reentry_aura_enabled: String(enabled),
          callback_reentry_max_depth: String(maxDepth),
          callback_reentry_max_iterations: String(maxIterations),
        },
      });
      await load();
    } catch (err) {
      console.error("Failed to save callback re-entry settings:", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="core-prompt-section">
      <p className="prompt-description">
        Central callback re-entry controls for delegated Aura results. These limit how many times Anna can re-enter full analysis and re-delegate again after an Aura callback.
      </p>
      {loading ? (
        <div className="placeholder">Loading callback re-entry settings…</div>
      ) : (
        <div className="prompt-section">
          <div className="variable-item" style={{ marginBottom: "0.75rem" }}>
            <label className="variable-label" style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
              Enable Aura callback re-entry
            </label>
          </div>
          <div style={{ display: "flex", gap: "1rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
            <label className="variable-item" style={{ flex: "1 1 180px" }}>
              <div className="variable-label">Max callback depth</div>
              <input className="instruction-category-input" type="number" min="0" max="10" value={maxDepth} onChange={(e) => setMaxDepth(e.target.value)} />
            </label>
            <label className="variable-item" style={{ flex: "1 1 180px" }}>
              <div className="variable-label">Max Anna passes</div>
              <input className="instruction-category-input" type="number" min="1" max="8" value={maxIterations} onChange={(e) => setMaxIterations(e.target.value)} />
            </label>
          </div>
          <button className="btn-primary" type="button" onClick={save} disabled={saving}>
            {saving ? "Saving…" : "Save callback re-entry settings"}
          </button>
        </div>
      )}
    </div>
  );
}

function IterationCard({ message, idx }) {
  const role = message.role;
  if (role === "system" || role === "user") {
    return (
      <div className="prompt-log-detail" style={{
        border: "1px dashed #aab", borderRadius: 6, padding: "0.5rem", marginBottom: "0.5rem",
      }}>
        <div className="section-stat">#{idx} · {role}</div>
        <pre className="prompt-content">{message.content || "(empty)"}</pre>
      </div>
    );
  }
  if (role === "assistant") {
    const toolCalls = message.tool_calls || [];
    return (
      <div className="prompt-log-detail" style={{
        border: "1px solid #ccd", borderRadius: 6, padding: "0.75rem", marginBottom: "0.75rem",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem",
        }}>
          <strong>#{idx} · Aura</strong>
          <span className="section-stat">{toolCalls.length} tool call(s)</span>
        </div>
        {message.thinking && (
          <CollapsibleBlock title="Thinking" stat={`${formatNumber(message.thinking.length)} chars`}>
            <pre className="prompt-content" style={{ opacity: 0.85 }}>{message.thinking}</pre>
          </CollapsibleBlock>
        )}
        {message.content && (
          <CollapsibleBlock
            title="Response"
            defaultOpen={!toolCalls.length}
            stat={`${formatNumber(message.content.length)} chars`}
          >
            <pre className="prompt-content">{message.content}</pre>
          </CollapsibleBlock>
        )}
        {toolCalls.length > 0 && (
          <CollapsibleBlock title={`Tool calls (${toolCalls.length})`} defaultOpen={true}>
            {toolCalls.map((tc, i) => {
              const fn = tc.function || {};
              const args = typeof fn.arguments === "string"
                ? (() => { try { return JSON.parse(fn.arguments); } catch { return fn.arguments; } })()
                : fn.arguments;
              return (
                <div key={i} className="variable-item" style={{ marginBottom: "0.5rem" }}>
                  <div className="variable-name">
                    <span className="variable-label">▶ {fn.name || "(unknown)"}</span>
                  </div>
                  <pre className="variable-value">{
                    typeof args === "string" ? args : JSON.stringify(args, null, 2)
                  }</pre>
                </div>
              );
            })}
          </CollapsibleBlock>
        )}
      </div>
    );
  }
  if (role === "tool") {
    let parsed = message.content;
    let isError = false;
    try {
      const obj = typeof message.content === "string" ? JSON.parse(message.content) : message.content;
      if (obj && typeof obj === "object") {
        parsed = obj;
        if (obj.success === false || obj.error) isError = true;
      }
    } catch { /* leave as raw string */ }
    return (
      <div className="prompt-log-detail" style={{
        border: "1px solid #cdc", borderRadius: 6, padding: "0.75rem", marginBottom: "0.75rem",
        background: isError ? "rgba(200,50,50,0.04)" : "rgba(0,160,0,0.04)",
      }}>
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem",
        }}>
          <strong>#{idx} · {isError ? "❌" : "✅"} {message.name || "tool"}</strong>
        </div>
        <pre className="prompt-content">{
          typeof parsed === "string" ? parsed : JSON.stringify(parsed, null, 2)
        }</pre>
      </div>
    );
  }
  return null;
}

// ---- Aura's Core (prompt editor, identical pattern to Irene) ----

function AuraCore() {
  const [prompt, setPrompt] = useState("");
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [updatedAt, setUpdatedAt] = useState("");
  const [activeVersion, setActiveVersion] = useState(0);
  const [versions, setVersions] = useState([]);

  const estimateTokens = (text) => Math.ceil((text || "").length / 4);

  const fetchPrompt = useCallback(async () => {
    try {
      const data = await apiClient.get("/aura/prompt");
      setPrompt(data.prompt || "");
      setUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || 0);
      setVersions(data.versions || []);
    } catch (err) {
      console.error("Failed to fetch aura prompt:", err);
    }
  }, []);

  useEffect(() => { fetchPrompt(); }, [fetchPrompt]);

  const handleSave = async () => {
    setLoading(true);
    setSaved(false);
    try {
      const data = await apiClient.put("/aura/prompt", { prompt });
      setUpdatedAt(data.updatedAt || "");
      setActiveVersion(data.activeVersion || data.version || 0);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
      const refreshed = await apiClient.get("/aura/prompt");
      setVersions(refreshed.versions || []);
    } catch (err) {
      console.error("Failed to save aura prompt:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateVersion = async (version) => {
    if (!window.confirm(`Restore Aura prompt version ${version}?`)) return;
    setLoading(true);
    try {
      await apiClient.put("/aura/prompt", { activateVersion: version });
      await fetchPrompt();
    } catch (err) {
      console.error("Failed to activate aura version:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="core-prompt-section">
      <p className="prompt-description">
        Aura's core system prompt — used when Anna dispatches a smart-home task.
        Custom household rules from the Instructions tab are appended automatically.
        {activeVersion > 0 && <span className="prompt-version-badge">v{activeVersion}</span>}
      </p>
      <div className="prompt-token-bar">
        <span>{prompt.length.toLocaleString()} chars</span>
        <span className="prompt-token-sep">·</span>
        <span>~{estimateTokens(prompt).toLocaleString()} tokens</span>
      </div>
      <textarea
        className="prompt-editor"
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Enter Aura's core prompt..."
        spellCheck={false}
      />
      <div className="prompt-actions">
        <button className="btn-primary" onClick={handleSave} disabled={loading}>
          {loading ? "Saving..." : "Save as New Version"}
        </button>
        {saved && <span className="save-confirm">✓ Saved as v{activeVersion}</span>}
        {updatedAt && (
          <span className="prompt-updated">
            Last updated: {new Date(updatedAt).toLocaleString()}
          </span>
        )}
      </div>

      {versions.length > 0 && (
        <div className="prompt-versions">
          <h3>Version History</h3>
          <div className="versions-list">
            {versions.map((v) => (
              <div
                key={v.version}
                className={`version-item ${v.version === activeVersion ? "active" : ""}`}
              >
                <div className="version-info">
                  <span className="version-number">v{v.version}</span>
                  {v.version === activeVersion && <span className="version-active-tag">Active</span>}
                  <span className="version-date">
                    {v.createdAt ? new Date(v.createdAt).toLocaleString() : "—"}
                  </span>
                  <span className="version-chars">
                    {v.charCount?.toLocaleString()} chars · ~{Math.ceil((v.charCount || 0) / 4).toLocaleString()} tokens
                  </span>
                </div>
                <div className="version-preview">{v.preview}</div>
                {v.version !== activeVersion && (
                  <button
                    className="btn-restore"
                    onClick={() => handleActivateVersion(v.version)}
                    disabled={loading}
                  >
                    Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Custom Instructions (CRUD) ----

function autoResize(el) {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = el.scrollHeight + "px";
}

function InstructionForm({ item, onSave, onCancel }) {
  const [content, setContent] = useState(item?.content || "");
  const [category, setCategory] = useState(item?.category || "");
  const [saving, setSaving] = useState(false);
  const textareaRef = useCallback((el) => {
    if (el) {
      autoResize(el);
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handleSubmit = async () => {
    if (!content.trim()) return;
    setSaving(true);
    try {
      await onSave({ content: content.trim(), category: category.trim() || undefined });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="version-item active">
      <textarea
        ref={textareaRef}
        className="prompt-editor auto-textarea"
        value={content}
        onChange={(e) => { setContent(e.target.value); autoResize(e.target); }}
        placeholder='e.g. "After 22:00 keep all lights below 30% brightness."'
        rows={1}
        spellCheck={false}
      />
      <input
        type="text"
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        placeholder="category (optional)"
        className="instruction-category-input"
      />
      <div className="instruction-form-actions">
        <button className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn-primary" onClick={handleSubmit} disabled={saving || !content.trim()}>
          {saving ? "Saving…" : item ? "Update" : "Add"}
        </button>
      </div>
    </div>
  );
}

function AuraInstructions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/aura/instructions");
      setItems(data.items || []);
    } catch (err) {
      console.error("Failed to fetch aura instructions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleCreate = async ({ content, category }) => {
    await apiClient.post("/aura/instructions", { content, category, active: true });
    setCreating(false);
    await fetchItems();
  };

  const handleUpdate = async (item, { content, category }) => {
    await apiClient.put(`/aura/instructions/${item.id}`, { content, category });
    setEditingId(null);
    await fetchItems();
  };

  const handleToggle = async (item) => {
    try {
      await apiClient.put(`/aura/instructions/${item.id}`, { active: !item.active });
      await fetchItems();
    } catch (err) {
      console.error("Failed to toggle instruction:", err);
    }
  };

  const handleDelete = async (item) => {
    try {
      await apiClient.delete(`/aura/instructions/${item.id}`);
      setDeletingId(null);
      await fetchItems();
    } catch (err) {
      console.error("Failed to delete instruction:", err);
    }
  };

  return (
    <div className="core-prompt-section">
      <p className="prompt-description">
        Custom rules appended to Aura's prompt at runtime. Use these for
        household-specific behaviour (e.g. "never turn off the bedroom lamp before
        23:00", "the lamp by the couch is called 'reading lamp'").
      </p>

      <div className="prompt-section">
        <div className="section-header">
          <h4>Rules ({items.filter((i) => i.active).length} active / {items.length} total)</h4>
          <div className="skills-stats-actions">
            <RefreshButton onClick={fetchItems} title="Refresh instructions" />
            {!creating && (
              <button
                className="icon-btn icon-btn-add"
                onClick={() => { setCreating(true); setEditingId(null); }}
                title="New rule"
                aria-label="New rule"
              >
                +
              </button>
            )}
          </div>
        </div>

        {creating && (
          <InstructionForm onSave={handleCreate} onCancel={() => setCreating(false)} />
        )}

        {loading ? (
          <div className="placeholder">Loading…</div>
        ) : items.length === 0 ? (
          <div className="placeholder">No custom rules yet.</div>
        ) : (
          <div className="versions-list">
            {items.map((it) =>
              editingId === it.id ? (
                <InstructionForm
                  key={it.id}
                  item={it}
                  onSave={(vals) => handleUpdate(it, vals)}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div
                  key={it.id}
                  className={`version-item ${it.active ? "active" : ""}`}
                  style={{ opacity: it.active ? 1 : 0.55 }}
                >
                  <div className="version-info">
                    <span className="version-number">{it.active ? "● ON" : "○ off"}</span>
                    {it.category && (
                      <span className="version-active-tag">{it.category}</span>
                    )}
                    <span className="version-date">
                      {it.createdAt ? new Date(it.createdAt).toLocaleDateString() : "—"}
                    </span>
                  </div>
                  <div className="version-preview" style={{ whiteSpace: "pre-wrap" }}>
                    {it.content}
                  </div>
                  {deletingId === it.id ? (
                    <div className="instruction-confirm-bar">
                      <span>Delete this rule?</span>
                      <button className="btn-danger btn-xs" onClick={() => handleDelete(it)}>Yes, delete</button>
                      <button className="btn-secondary btn-xs" onClick={() => setDeletingId(null)}>Cancel</button>
                    </div>
                  ) : (
                    <div className="instruction-actions">
                      <button className="btn-card-action" onClick={() => handleToggle(it)}>
                        {it.active ? "Disable" : "Enable"}
                      </button>
                      <button className="btn-card-action" onClick={() => { setEditingId(it.id); setCreating(false); }}>
                        Edit
                      </button>
                      <button className="btn-card-action btn-card-danger" onClick={() => setDeletingId(it.id)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Sessions list ----

function SessionsList() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [hydrated, setHydrated] = useState({});
  const [hydrating, setHydrating] = useState({});

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.get("/aura/sessions?limit=50");
      setSessions(data.items || []);
    } catch (err) {
      console.error("Failed to fetch aura sessions:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const toggleExpand = async (s) => {
    if (expandedId === s.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(s.id);
    const needsHydration =
      (s.spillover && (s.spillover.bucket || s.spillover.key)) || s.truncated;
    if (needsHydration && !hydrated[s.id] && !hydrating[s.id]) {
      setHydrating((h) => ({ ...h, [s.id]: true }));
      try {
        const data = await apiClient.get(`/aura/sessions?id=${encodeURIComponent(s.id)}`);
        if (data && data.item) {
          setHydrated((h) => ({ ...h, [s.id]: data.item }));
        }
      } catch (err) {
        console.error("Failed to hydrate aura session:", err);
      } finally {
        setHydrating((h) => ({ ...h, [s.id]: false }));
      }
    }
  };

  if (loading) return <div className="placeholder">Loading Aura sessions…</div>;
  if (sessions.length === 0) {
    return (
      <div className="placeholder">
        No Aura sessions yet. Ask Anna to control the lights, TV, or receiver.
      </div>
    );
  }

  return (
    <div className="output-analysis">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem" }}>
        <p className="prompt-analysis-description">
          Aura is Anna's dedicated smart-home agent. Each row is one task —
          the full Gemma↔devices transcript with WiZ, TV, and Denon.
          tool calls. Items larger than ~350 KB spill to S3 and are hydrated on click.
        </p>
        <RefreshButton onClick={fetchSessions} title="Refresh Aura sessions" />
      </div>

      <div className="prompt-logs-list">
        {sessions.map((s) => {
          const isExpanded = expandedId === s.id;
          const full = hydrated[s.id] || s;
          const messages = full.messages || [];
          return (
            <div key={s.id} className={`prompt-log-item ${isExpanded ? "expanded" : ""}`}>
              <div className="prompt-log-header" onClick={() => toggleExpand(s)}>
                <div className="prompt-log-meta">
                  <span className="prompt-log-source">aura</span>
                  <span className="prompt-log-time">
                    {s.timestamp ? new Date(s.timestamp).toLocaleString() : "—"}
                  </span>
                  <span className="prompt-log-session" title={s.taskId}>
                    {s.taskId?.slice(0, 12)}
                  </span>
                </div>
                <div className="prompt-log-preview">
                  {(s.task || "(no task)").slice(0, 120)}
                  {(s.task || "").length > 120 ? "…" : ""}
                </div>
                <div className="prompt-log-tokens">
                  {statusBadge(s.status)}
                  <span className="token-badge total">{s.iterationCount || 0} iter</span>
                  {s.toolCallCount > 0 && (
                    <span className="token-badge chat">{s.toolCallCount} tool</span>
                  )}
                  {s.elapsedSeconds > 0 && (
                    <span className="token-badge skills">{s.elapsedSeconds}s</span>
                  )}
                  {s.clarificationRound > 0 && (
                    <span className="token-badge user">clarif #{s.clarificationRound}</span>
                  )}
                  {s.spillover?.bucket && (
                    <span className="token-badge user" title={s.spillover.key}>S3</span>
                  )}
                  {s.truncated && (
                    <span className="token-badge user" style={{ color: "#c33" }}>truncated</span>
                  )}
                </div>
              </div>

              {isExpanded && (
                <div className="prompt-log-detail">
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem",
                  }}>
                    <span className="section-stat">
                      {hydrating[s.id] ? "Hydrating from S3…" : `task_id ${s.taskId}`}
                    </span>
                    <button type="button" className="section-tab" onClick={() => copyJson(full)}>
                      Copy as JSON
                    </button>
                  </div>

                  <CollapsibleBlock title="Task" defaultOpen={true}>
                    <pre className="prompt-content">{full.task || "(empty)"}</pre>
                    {full.dispatchedTask && full.dispatchedTask !== full.task && (
                      <>
                        <div className="section-header" style={{ marginTop: "0.5rem" }}>
                          <h4>Dispatched (with clarification)</h4>
                        </div>
                        <pre className="prompt-content">{full.dispatchedTask}</pre>
                      </>
                    )}
                  </CollapsibleBlock>

                  {full.report && (
                    <CollapsibleBlock title="Report" defaultOpen={true} stat={`${formatNumber(full.report.length)} chars`}>
                      <pre className="prompt-content">{full.report}</pre>
                    </CollapsibleBlock>
                  )}
                  {full.question && (
                    <CollapsibleBlock title="Question to user" defaultOpen={true}>
                      <pre className="prompt-content">{full.question}</pre>
                    </CollapsibleBlock>
                  )}
                  {full.error && (
                    <CollapsibleBlock title="Error" defaultOpen={true}>
                      <pre className="prompt-content" style={{ color: "#c33" }}>{full.error}</pre>
                    </CollapsibleBlock>
                  )}

                  <CollapsibleBlock
                    title={`Transcript (${messages.length} messages)`}
                    defaultOpen={false}
                  >
                    {messages.length === 0 ? (
                      <p className="empty-section">no transcript</p>
                    ) : (
                      messages.map((m, i) => <IterationCard key={i} message={m} idx={i} />)
                    )}
                  </CollapsibleBlock>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---- Main page with tabs ----

function AuraSessions() {
  const hashTab = window.location.hash.replace("#", "");
  const validTabs = ["sessions", "core", "instructions"];
  const [tab, setTab] = useState(validTabs.includes(hashTab) ? hashTab : "sessions");
  const switchTab = (t) => { setTab(t); window.location.hash = t; };

  return (
    <div className="annacore-page">
      <div className="annacore-header">
        <div>
          <h2>Aura</h2>
          <p className="annacore-subtitle">Smart-home agent on Semidiosa — sessions, core prompt, and custom rules</p>
        </div>
      </div>

      <div className="tab-bar">
        <button className={`tab ${tab === "sessions" ? "active" : ""}`} onClick={() => switchTab("sessions")}>
          🏠 Sessions
        </button>
        <button className={`tab ${tab === "core" ? "active" : ""}`} onClick={() => switchTab("core")}>
          🧠 Aura's Core
        </button>
        <button className={`tab ${tab === "instructions" ? "active" : ""}`} onClick={() => switchTab("instructions")}>
          📜 Instructions
        </button>
      </div>

      <CallbackReentrySettings />

      {tab === "sessions" && <SessionsList />}
      {tab === "core" && <AuraCore />}
      {tab === "instructions" && <AuraInstructions />}
    </div>
  );
}

export default AuraSessions;
